import { gql, useFragment } from "@apollo/client";
import { useRouter } from "next/router";

// Constants
import {
  imgFields,
  linkFields,
  layoutColSettings,
} from "@/constants/gql-fields";

// Styling
import style from "./header--columns.module.scss";

// Components
import Link from "next/link";
import LayoutColumn from "../layout-column/layout-column";
import Logo from "@/components/logo/logo";
import MenuWP from "../menu-wp/menu-wp";
import SocialIcons from "@/components/social-icons/social-icons";
import SearchIcon from "@/components/search-icon/search-icon";
// import LayoutSearch from "../layout-search/layout-search";
import Searchbar from "@/components/searchbar/searchbar";
import Button from "@/components/button/button";

export default function HeaderColumns({
  headerData,
  navMobileState = {},
  setNavMobileState = {},
  searchClickState = {},
}) {
  // -- useFragment
  const navProps = useFragment({
    fragment: HeaderColumns.fragments.menu,
    fragmentName: "HeaderMenus",
    from: {
      __typename: "RootQuery",
    },
  });

  const headerProps = useFragment({
    fragment: HeaderColumns.fragments.header,
    fragmentName: "HeaderColGenSet",
    from: {
      __typename: headerData.__typename,
      id: headerData.id,
    },
  });
  if (!navProps.complete || !headerProps.complete) {
    return;
  }

  const router = useRouter();

  // -- Define Variables
  const headerSettings = headerProps.data.headerSettings;
  const headerContent = headerProps.data.headerContent;
  const headerCols = headerContent.headerColumns;
  let headerColumnsStyle = headerSettings.layoutType
    ? "headerColumnsFlex"
    : "headerColumnsGrid";
  const subMenuIcons = headerSettings.options.includes("submenu-icons");
  const mobileNavColor =
    router.asPath == "/ingredients" ? "#007cce" : "#1fb4ff";

  // -- Breakpoints
  const customBreakpoints = headerSettings.options.includes("breakpoints")
    ? true
    : false;
  const bpMD =
    customBreakpoints && headerSettings.custom_bp_md
      ? headerSettings.custom_bp_md
      : "800";
  const bpLG =
    customBreakpoints && headerSettings.custom_bp_lg
      ? headerSettings.custom_bp_lg
      : "992";

  function handleMobileNavClick() {
    setNavMobileState(navMobileState == "closed" ? "open" : "closed");
    document.body.setAttribute(
      "mobile-state",
      navMobileState == "closed" ? "open" : "closed"
    );
  }

  return (
    <div
      className={`${style.headerColumns} ${style[headerColumnsStyle]}`}
      breakpoints={customBreakpoints ? "custom" : "regular"}
    >
      {headerCols.map((col, index) => {
        const colItems = col.headerColumnItems;
        const colCount = index + 1;

        // Layout Column Variables
        const columnAttributes = {
          colHori: col.columnHorizontalOrientation,
          colVert: col.columnVerticalOrientation,
          content: col.horizontalContent ? "horizontal" : "vertical",
        };
        const columnStyleItems = {
          colCount: colCount,
          layoutType: headerSettings.layoutType ? "flex" : "grid",
          breakpoints: {
            md: bpMD,
            lg: bpLG,
          },
          colFlex: [
            col.columnSizeFlex,
            col.columnFlexLgPlus,
            col.columnFlexMdMinus,
            col.columnFlexMdToLg,
          ],
          colGrid: [
            col.columnSizeGrid,
            col.columnGridLgPlus,
            col.columnGridMdMinus,
            col.columnGridMdToLg,
          ],
        };

        return (
          <LayoutColumn
            columnAttributes={columnAttributes}
            columnStyleItems={columnStyleItems}
            location="header"
            key={index}
          >
            {colItems.map((item, index) => {
              // Must add key={index} to each item for the map - will not affect the component but will thrown an error without it //

              switch (item.__typename) {
                // Logo
                case "GeneralSettingsACFHeaderContentHeaderColumnsHeaderColumnItemsLogoLayout":
                  return <Logo key={index} entry={headerProps.data.logo} />;

                // Main Nav
                case "GeneralSettingsACFHeaderContentHeaderColumnsHeaderColumnItemsMainNavLayout":
                  return (
                    <MenuWP
                      menu={navProps.data.navMain}
                      caratShow={subMenuIcons}
                      navStyle="headerNav"
                      key={index}
                    />
                  );

                // Top Nav
                case "GeneralSettingsACFHeaderContentHeaderColumnsHeaderColumnItemsTopNavLayout":
                  return (
                    <MenuWP
                      menu={navProps.data.navTop}
                      caratShow={subMenuIcons}
                      navStyle="topNav"
                      key={index}
                    />
                  );

                // Mobile Nav Button
                case "GeneralSettingsACFHeaderContentHeaderColumnsHeaderColumnItemsMobileNavButtonLayout":
                  return (
                    <Button
                      id="mobileNav"
                      className={style.navMobileButton}
                      state={navMobileState}
                      onClick={handleMobileNavClick}
                      dataAtts={{
                        "data-toggle": "collapse",
                        "data-target": "#nav--mobile",
                      }}
                      ariaAtts={{
                        "aria-label":
                          navMobileState == "closed"
                            ? "Expand mobile menu"
                            : "Close mobile menu",
                      }}
                      key={index}
                    >
                      <span
                        className="icon-bar"
                        style={{ backgroundColor: mobileNavColor }}
                      ></span>
                      <span
                        className="icon-bar"
                        style={{ backgroundColor: mobileNavColor }}
                      ></span>
                      <span
                        className="icon-bar"
                        style={{ backgroundColor: mobileNavColor }}
                      ></span>
                      <span
                        className="icon-bar"
                        style={{ backgroundColor: mobileNavColor }}
                      ></span>
                      <span className="sr-only">Toggle navigation</span>
                    </Button>
                  );

                // Social Media Icons
                case "GeneralSettingsACFHeaderContentHeaderColumnsHeaderColumnItemsSocialMediaIconsLayout":
                  const socialHasSearch = item.includeSearchIcon;
                  return (
                    <div className={style.headerIcons} key={index}>
                      <SocialIcons id={headerProps.data.id} />
                      {socialHasSearch && (
                        <SearchIcon
                          entry={headerProps.data.searchSettings}
                          searchClickState={searchClickState}
                        />
                      )}
                    </div>
                  );

                // Search
                case "GeneralSettingsACFHeaderContentHeaderColumnsHeaderColumnItemsSearchLayout":
                  // const searchType = item.searchType;

                  if (router.pathname == "/") {
                    return;
                  }
                  return (
                    // <LayoutSearch
                    //   searchType={searchType}
                    //   searchIconSettings={headerProps.data.searchSettings}
                    //   key={index}
                    //   searchClickState={searchClickState}
                    // />
                    <Searchbar key={index} />
                  );

                // Custom Text
                case "GeneralSettingsACFHeaderContentHeaderColumnsHeaderColumnItemsCustomTextLayout":
                  const customText = item.text;
                  return (
                    <div
                      className="headerCustomText"
                      key={index}
                      dangerouslySetInnerHTML={{ __html: customText }}
                    ></div>
                  );

                // Custom Image
                case "GeneralSettingsACFHeaderContentHeaderColumnsHeaderColumnItemsCustomImageLayout":
                  const customImage = item.image.node;
                  const imgHasLink = item.hasLink;
                  const imgLink = item.imageLink;
                  const imgMaxWidth = item.maxWidth;

                  let imgStyling = "";
                  if (imgMaxWidth) {
                    imgStyling = {
                      maxWidth: imgMaxWidth,
                    };
                  }

                  let imgItem = (
                    <img
                      src={customImage.sourceUrl}
                      alt={customImage.altText ?? ""}
                      style={imgStyling}
                    />
                  );

                  return (
                    <div className="headerCustomImage" key={index}>
                      {imgHasLink && (
                        <Link href={imgLink.url} target={imgLink.target}>
                          {imgItem}
                        </Link>
                      )}
                      {imgItem}
                    </div>
                  );

                // Default
                default:
                  return null;
              }
            })}
          </LayoutColumn>
        );
      })}
    </div>
  );
}

HeaderColumns.fragments = {
  menu: gql`
    fragment HeaderMenus on RootQuery {
      navMain: menu(id: "3", idType: DATABASE_ID) {
        # menu(id: $navID, idType: DATABASE_ID) {
        id
        __typename
        ...WpMenuFragment @unmask(mode: "migrate")
      }
      navTop: menu(id: "207", idType: DATABASE_ID) {
        # menu(id: $navID, idType: DATABASE_ID) {
        id
        __typename
        ...WpMenuFragment @unmask(mode: "migrate")
      }
    }
    ${MenuWP.fragments.menu}
  `,
  header: gql`
    fragment HeaderColGenSet on GeneralSettingsACF {
      id
      __typename
      headerSettings {
        options
        custom_bp_md
        custom_bp_lg
        layoutType
      }
      headerContent {
        headerColumns {
          ${layoutColSettings}
          headerColumnItems {
            __typename
            ... on GeneralSettingsACFHeaderContentHeaderColumnsHeaderColumnItemsSocialMediaIconsLayout {
              includeSearchIcon
            }
            ... on GeneralSettingsACFHeaderContentHeaderColumnsHeaderColumnItemsSearchLayout {
              searchType
            }
            ... on GeneralSettingsACFHeaderContentHeaderColumnsHeaderColumnItemsCustomTextLayout {
              text
            }
            ... on GeneralSettingsACFHeaderContentHeaderColumnsHeaderColumnItemsCustomImageLayout {
              hasLink
              maxWidth
              image {
                ${imgFields}
              }
              imageLink {
                ${linkFields}
              }
            }
          }
        }
      }
      logo {
        id
        __typename
        ...LogoFragment @unmask(mode: "migrate")
      }
      searchSettings {
        id
        __typename
        ...SearchIconFragment @unmask(mode: "migrate")
      }
      ...SocialIconsFragment @unmask(mode: "migrate")
    }
    ${Logo.fragments.entry}
    ${SocialIcons.fragments.entry}
    ${SearchIcon.fragments.entry}
  `,
};
